import type { Messages } from "../types";

export const messages: Messages = {
  shared: {
    title: "Account cliente Fielmann",
    tabs: {
      login: "Accedi",
      signup: "Crea un account",
      ariaLabel: "Autenticazione",
    },
    languageSwitcher: {
      label: "Seleziona lingua",
    },
    password: {
      show: "Mostra password",
      hide: "Nascondi password",
    },
    header: {
      logoAriaLabel: "Fielmann",
    },
    footer: {
      ariaLabel: "Piè di pagina",
      customerService: "Servizio Clienti",
      legalNotice: "Note legali",
      dataProtection: "Privacy",
    },
    loading: {
      ariaLabel: "Un momento...",
      content: "Caricamento dei contenuti in corso.",
    },
    genericError: "Si è verificato un errore. Riprova più tardi.",
  },
  login: {
    subtitle: "Accedi con la tua e-mail e la tua password.",
    form: {
      emailLabel: "E-mail *",
      passwordLabel: "Password *",
      forgotPassword: "Password dimenticata?",
      submitButton: "Accedi",
      credentialError: "E-mail o password errati.",
    },
  },
  signup: {
    subtitle: {
      before: "Approfitta dei ",
      highlight: "vantaggi",
      after: " e crea il tuo account cliente Fielmann.",
    },
    benefits: {
      popoverHeading: "I tuoi vantaggi:",
      closeLabel: "Chiudi",
      items: {
        orders:
          "Visualizzare tutti gli ordini Fielmann e verificarne lo stato.",
        visionValues: "Riutilizzare i valori visivi per il prossimo ordine.",
        appointments: "Prendere e gestire gli appuntamenti.",
        reorders: "Riordinare facilmente online.",
      },
    },
    form: {
      firstNameLabel: "Nome *",
      lastNameLabel: "Cognome *",
      emailLabel: "E-mail *",
      passwordLabel: "Password *",
      passwordRepeatLabel: "Conferma password *",
      submitButton: "Crea un account",
      errorTitle: "Non è stato possibile creare l’account.",
      errorMessage: "Controlla i dati inseriti o riprova più tardi.",
    },
    consents: {
      requiredError: "Fornisci il consenso richiesto.",
      unavailableError: "Si è verificato un errore. Riprova più tardi.",
      required: {
        headline: "Consenso alla creazione dell’account cliente",
        subline:
          "Per poterti mostrare tutti i contenuti del tuo account (ad es. acquisti o appuntamenti), abbiamo bisogno del tuo consenso.",
      },
      optional: {
        headline: "Vuoi ricevere pubblicità personalizzata?",
        subline:
          "Puoi scegliere: se acconsenti, riceverai offerte e informazioni personalizzate. In caso contrario, potrai comunque utilizzare il tuo account, ma senza pubblicità personalizzata.",
      },
    },
  },
  emailVerification: {
    subtitle: "Non riesci ad accedere al tuo account?",
    alertTitle: "Prima conferma il tuo indirizzo e-mail.",
    alertBody:
      "Conferma prima il tuo indirizzo e-mail per attivare il tuo account Fielmann.",
    alertSpamHint: "Non trovi l'e-mail? Controlla anche la cartella Spam.",
    resendButton: "Richiedi nuovamente l’e-mail",
    backToLogin: "Torna al login",
  },
  resetPasswordRequest: {
    subtitle: "Hai dimenticato la password?",
    body: "Inserisci l'indirizzo e-mail con cui hai creato il tuo account. Ti invieremo un link per reimpostare la password.",
    emailLabel: "E-mail *",
    submitButton: "Richiedi il link",
    backToLogin: "Torna al login",
    banner: {
      closeAria: "Chiudi",
      rateLimit: {
        title: "Hai richiesto l'e-mail troppe volte",
        body: "Controlla la tua casella di posta e la cartella Spam. L'e-mail per reimpostare la password potrebbe essere lì.",
        waitHint:
          "Non riesci ancora a trovarla? Attendi qualche istante: l'e-mail potrebbe essere ancora in arrivo.",
      },
      error: {
        title: "Invio non riuscito",
        body: "Riprova più tardi.",
      },
    },
  },
  resetPasswordEmail: {
    success: {
      heading: "E-mail per reimpostare la password inviata.",
      body: "Ti abbiamo inviato un'e-mail per reimpostare la password. Controlla la tua casella di posta.",
      spamHint: "Non trovi l'e-mail? Controlla anche la cartella Spam.",
    },
    restart: {
      hint: "Se ancora non trovi l'e-mail, puoi ripetere la procedura. Forse hai utilizzato un altro indirizzo e-mail.",
      button: "Riprova",
    },
    error: {
      heading: "Invio non riuscito",
      body: "Riprova più tardi.",
      retryButton: "Riprova",
      rateLimitBody:
        "Troppi tentativi. Attendi qualche minuto prima di riprovare.",
    },
  },
  setPassword: {
    subtitle: "Ora puoi impostare la tua password.",
    passwordLabel: "Password *",
    passwordRepeatLabel: "Conferma password *",
    submitButton: "Salva password",
    backToLogin: "Vai al login",
    successTitle: "La password è stata salvata correttamente.",
    successRedirectBody: "Stai per essere reindirizzato alla nostra homepage.",
    successRedirectLoginHint:
      "Da lì potrai accedere con la tua nuova password.",
    successWaitHint: "Un momento, per favore.",
  },
  resetPasswordError: {
    expired: {
      heading: "Questo link è scaduto.",
      body: 'Il link per reimpostare la password non è più valido. Torna alla homepage e ripeti la procedura selezionando "Password dimenticata?" nella pagina di login.',
    },
    used: {
      heading: "Questo link è già stato utilizzato.",
      body: 'Questo link è già stato utilizzato per reimpostare la password. Se desideri modificarla di nuovo, torna alla homepage e ripeti la procedura selezionando "Password dimenticata?" nella pagina di login.',
    },
    generic: {
      heading: "Questo link non è valido.",
      body: 'Non è stato possibile utilizzare il link. Torna alla homepage e ripeti la procedura selezionando "Password dimenticata?" nella pagina di login.',
    },
    backToHome: "Torna alla homepage",
  },
  toasts: {
    missingRequiredFields: "Compila tutti i campi obbligatori per continuare.",
  },
  validation: {
    passwordRequired: "Inserisci una password.",
    passwordRepeatRequired: "Inserisci nuovamente la password.",
    firstNameTooLong: "Il nome non può superare i 30 caratteri.",
    firstNameRequired: "Inserisci il nome.",
    firstNameInvalidFormat: "Inserisci un nome valido.",
    lastNameTooLong: "Il cognome non può superare i 30 caratteri.",
    lastNameRequired: "Inserisci il cognome.",
    lastNameInvalidFormat: "Inserisci un cognome valido.",
    emailTooLong: "L’indirizzo e-mail è troppo lungo.",
    emailRequired: "Inserisci un indirizzo e-mail.",
    emailInvalidFormat: "Inserisci un indirizzo e-mail valido.",
    passwordMatchesEmail:
      "La password deve essere diversa dall’indirizzo e-mail.",
    passwordMismatch: "Le password non corrispondono.",
  },
  passwordRequirements: {
    minimumLength: "Almeno 10 caratteri",
    mixedCase: "Almeno una lettera maiuscola e una minuscola",
    hasNumber: "Almeno un numero",
    specialCharactersAllowed: "Caratteri speciali consentiti (ad es. !@#$%^&*)",
  },
};
